import {httpsCallable} from "firebase/functions";
import {functions} from "../firebase";
import {
    addDoc,
    collection,
    updateDoc,
    doc,
    serverTimestamp,
    query,
    where,
    getDocs
} from "../access/firestore";

import {
    ref,
    uploadBytes,
    getDownloadURL
} from "firebase/storage";

import {
    db,
    storage,
    auth
} from "../firebase";

import {
    generateReceiptPDF
} from "../utils/generateReceiptPDF";

import {
    sendWhatsAppFileVPS
} from "./whatsappVPS";

export async function registerPayment({

    clinicId,

    clinicData,

    paciente,

    cuenta,

    monto,

    metodoPago,

    enviarReciboWhatsApp = true

}) {

    // =====================================
    // VALIDACIONES
    // =====================================

    if (!clinicId) {

        throw new Error(
            "No se encontró la clínica."
        );

    }

    if (!cuenta) {

        throw new Error(
            "No se recibió una cuenta por cobrar."
        );

    }

    const pagoMonto =
        Number(monto);

    if (
        !pagoMonto ||
        pagoMonto <= 0
    ) {

        throw new Error(
            "Monto inválido."
        );

    }

    const usuarioActual = auth.currentUser;

    if (!usuarioActual) {
        throw new Error(
            "No hay un usuario autenticado para registrar el pago."
        );
    }

    const usuarioAuditoria = {
        createdBy: usuarioActual.uid,
        createdByName:
            usuarioActual.displayName ||
            usuarioActual.email ||
            "Usuario sin nombre",
        createdByEmail: usuarioActual.email || "",
        updatedBy: usuarioActual.uid,
        updatedByName:
            usuarioActual.displayName ||
            usuarioActual.email ||
            "Usuario sin nombre",
        updatedByEmail: usuarioActual.email || ""
    };
    const updateAudit = Object.fromEntries(Object.entries(usuarioAuditoria).filter(([key])=>!key.startsWith("createdBy")));


    const auditOperationId =
        `pago_${Date.now()}_${usuarioActual.uid}`;

    // =====================================
    // REFERENCIA DE CUENTAS POR COBRAR
    // =====================================

    const cuentasRef =
        collection(
            db,
            "clinics",
            clinicId,
            "pacientes",
            paciente.id,
            "cuentasPorCobrar"
        );

    // =====================================
    // OBTENER CUENTA REAL DE FIRESTORE
    // =====================================

    let cuentaActual = null;

    const cuentaSnap =
        await getDocs(

            query(
                cuentasRef,
                where(
                    "__name__",
                    "==",
                    cuenta.id
                )
            )

        );

    if (!cuentaSnap.empty) {

        cuentaActual =
            cuentaSnap.docs[0];

    }

    if (!cuentaActual) {

        throw new Error(
            "No existe la cuenta por cobrar seleccionada."
        );

    }

    // =====================================
    // FIRESTORE = FUENTE DE VERDAD
    // =====================================

    const cuentaData =
        cuentaActual.data();

    const cuentaId =
        cuentaActual.id;

    // =====================================
    // TIPO DE CUENTA
    // =====================================

    const tipoCuenta =
        cuentaData.tipo ||
        "cita";

    const esPlanTratamiento =
        tipoCuenta === "planTratamiento";

    // =====================================
    // TOTAL REAL
    // =====================================

    const totalCuenta =
        Math.max(
            0,
            Number(
                cuentaData.total || 0
            )
        );

    // =====================================
    // PAGADO REAL
    //
    // La cuenta es ahora la fuente de verdad.
    // =====================================

    let pagadoActual =
        Number(
            cuentaData.pagado || 0
        );

    // =====================================
    // COMPATIBILIDAD CON CUENTAS ANTIGUAS
    //
    // Si una cuenta de plan fue creada con
    // pagado = 0 pero el plan tenía anticipo,
    // recuperamos el anticipo del plan.
    // =====================================

    if (
        esPlanTratamiento &&
        pagadoActual === 0 &&
        cuentaData.planId
    ) {

        const response = await httpsCallable(functions,"getTreatmentDownPayment")({clinicId,patientId:paciente.id,planId:cuentaData.planId});
        const anticipo = Number(response.data.downPayment || 0);
        {
            if (anticipo > 0) {

                pagadoActual =
                    Math.min(
                        anticipo,
                        totalCuenta
                    );

                await updateDoc(

                    doc(
                        db,
                        "clinics",
                        clinicId,
                        "pacientes",
                        paciente.id,
                        "cuentasPorCobrar",
                        cuentaId
                    ),

                    {

                        pagado:
                            pagadoActual,

                        saldo:
                            Math.max(
                                0,
                                totalCuenta -
                                pagadoActual
                            ),

                        estado:
                            totalCuenta -
                            pagadoActual > 0
                                ? "Pendiente"
                                : "Pagada"

                    }

                );

            }

        }

    }

    // =====================================
    // SALDO REAL
    // =====================================

    const saldoActual =
        Math.max(
            0,
            totalCuenta -
            pagadoActual
        );

    if (saldoActual <= 0) {

        throw new Error(
            "Esta cuenta ya está liquidada."
        );

    }

    if (pagoMonto > saldoActual) {

        throw new Error(
            "El pago excede el saldo pendiente."
        );

    }

    // =====================================
    // NUEVOS VALORES
    // =====================================

    const nuevoPagado =
        pagadoActual +
        pagoMonto;

    const saldoRestante =
        Math.max(
            0,
            totalCuenta -
            nuevoPagado
        );

    // =====================================
    // FOLIO
    // =====================================

    const folio =
        `REC-${Date.now()}`;

    // =====================================
    // CONCEPTO
    // =====================================

    const concepto =

        cuentaData.descripcion ||

        (
            esPlanTratamiento
                ? `Plan de tratamiento ${cuentaData.folio || ""}`.trim()
                : "Pago de cita"
        );

    // =====================================
    // GENERAR RECIBO
    // =====================================

    const pdf =
        await generateReceiptPDF({

            folio,

            clinicName:
                clinicData?.nombreClinica,

            clinicLogo:
                clinicData?.logoBase64,

            clinicAddress:
                clinicData?.direccion,

            clinicPhone:
                clinicData?.telefono,

            clinicRFC:
                clinicData?.RFC,

            clinicEmail:
                clinicData?.email,

            patientName:
                `${paciente.nombre || ""} ${paciente.apellidos || ""}`.trim(),

            treatment:
                concepto,

            paymentMethod:
                metodoPago,

            totalTreatment:
                totalCuenta,

            paymentAmount:
                pagoMonto,

            totalPaid:
                nuevoPagado,

            remainingBalance:
                saldoRestante,

            paymentDate:
                new Date().toLocaleString(
                    "es-MX"
                )

        });

    // =====================================
    // SUBIR RECIBO
    // =====================================

    const blob =
        pdf.output("blob");

    const fileName =
        `recibo_${Date.now()}.pdf`;

    const storageRef =
        ref(
            storage,
            `clinics/${clinicId}/receipts/${fileName}`
        );

    await uploadBytes(
        storageRef,
        blob
    );

    const downloadURL =
        await getDownloadURL(
            storageRef
        );

    // =====================================
    // REGISTRAR PAGO
    // =====================================

    const pagoData = {

        pacienteId:
            paciente.id,

        pacienteNombre:
            `${paciente.nombre || ""} ${paciente.apellidos || ""}`.trim(),

        monto:
            pagoMonto,

        metodoPago,

        concepto,

        presupuestoTotal:
            totalCuenta,

        saldoAnterior:
            saldoActual,

        saldoRestante,

        folio,

        reciboUrl:
            downloadURL,

        fecha:
            serverTimestamp(),

        cuentaPorCobrarId:
            cuentaId,

        tipoCuenta,

        auditHandled: true,

        auditOperationId,

            ...usuarioAuditoria

    };

    // =====================================
    // SI ES PLAN
    // =====================================

    if (esPlanTratamiento) {

        pagoData.planId =
            cuentaData.planId;

    }

    // =====================================
    // SI ES CITA
    // =====================================

    if (
        !esPlanTratamiento &&
        cuentaData.citaId
    ) {

        pagoData.citaId =
            cuentaData.citaId;

    }

    await addDoc(

        collection(
            db,
            "clinics",
            clinicId,
            "pagos"
        ),

        pagoData

    );

    // =====================================
    // GUARDAR RECIBO EN ARCHIVOS
    // =====================================

    await addDoc(

        collection(
            db,
            "clinics",
            clinicId,
            "pacientes",
            paciente.id,
            "files"
        ),

        {

            filename:
                fileName,

            mimeType:
                "application/pdf",

            fileUrl:
                downloadURL,

            source:
                "payment_receipt",

            folio,

            fromMe:
                true,

            createdAt:
                new Date()

        }

    );

    // =====================================
    // WHATSAPP
    // =====================================

    if (
        enviarReciboWhatsApp &&
        paciente.whatsapp
    ) {
        const resultadoWhatsApp =
            await sendWhatsAppFileVPS(
                clinicId,
                paciente.whatsapp,
                downloadURL,
                fileName,
                "Recibo de pago"
            );

        if (!resultadoWhatsApp?.success) {
            throw new Error(
                resultadoWhatsApp?.error ||
                "No se pudo enviar el recibo por WhatsApp."
            );
        }

        // Registrar el PDF enviado en el historial de WhatsApp.
        try {
            const conversacionesRef = collection(
                db,
                "clinics",
                clinicId,
                "whatsappConversations"
            );

            const conversacionesSnap =
                await getDocs(conversacionesRef);

            const normalizarTelefono = (valor) => {
                const digitos = String(valor || "")
                    .replace(/@c\.us$/i, "")
                    .replace(/\D/g, "");

                return digitos.length >= 10
                    ? digitos.slice(-10)
                    : digitos;
            };

            const telefonoPaciente =
                normalizarTelefono(paciente.whatsapp);

            const conversaciones =
                conversacionesSnap.docs.map((item) => ({
                    id: item.id,
                    ...item.data()
                }));

            // Priorizar la conversación vinculada al paciente.
            let conversacion = conversaciones.find(
                (item) => item.patientId === paciente.id
            );

            // Si no está vinculada, buscarla por teléfono.
            if (!conversacion) {
                conversacion = conversaciones.find((item) => {
                    const telefonoConversacion =
                        normalizarTelefono(
                            item.whatsapp || item.id
                        );

                    return (
                        telefonoConversacion &&
                        telefonoConversacion === telefonoPaciente
                    );
                });
            }

            if (!conversacion) {
                console.error(
                    "El recibo se envió, pero no se encontró " +
                    "la conversación para registrarlo en VexaDent.",
                    {
                        clinicId,
                        pacienteId: paciente.id,
                        telefono: paciente.whatsapp
                    }
                );
            } else {
                const mensajeRef = await addDoc(
                    collection(
                        db,
                        "clinics",
                        clinicId,
                        "whatsappConversations",
                        conversacion.id,
                        "messages"
                    ),
                    {
                        type: "file",
                        filename: fileName,
                        mimeType: "application/pdf",
                        fileUrl: downloadURL,
                        caption: "Recibo de pago",
                        fromMe: true,
                        status: "sent",
                        timestamp: new Date(),
                        source: "payment_receipt",
                        patientId: paciente.id,
                        patientName: (
                            `${paciente.nombre || ""} ` +
                            `${paciente.apellidos || ""}`
                        ).trim()
                    }
                );

                await updateDoc(
                    doc(
                        db,
                        "clinics",
                        clinicId,
                        "whatsappConversations",
                        conversacion.id
                    ),
                    {
                        lastMessage: `Recibo de pago: ${fileName}`,
                        lastMessageAt: serverTimestamp()
                    }
                );

                console.log(
                    "Recibo registrado en WhatsApp:",
                    mensajeRef.id
                );
            }
        } catch (error) {
            // No revertir el pago ni reenviar el PDF:
            // el envío por WhatsApp ya fue exitoso.
            console.error(
                "El recibo se envió, pero no se pudo registrar " +
                "en el historial de WhatsApp:",
                error
            );
        }
    }

    // =====================================
    // ACTUALIZAR CUENTA POR COBRAR
    // =====================================

    await updateDoc(

        doc(
            cuentasRef,
            cuentaId
        ),

        {

            pagado:
                nuevoPagado,

            saldo:
                saldoRestante,

            estado:
                saldoRestante > 0
                    ? "Pendiente"
                    : "Pagada",
            auditOperationId,
                ...updateAudit,

        }

    );

    // =====================================
    // ACTUALIZAR PLAN
    // =====================================

    if (
        esPlanTratamiento &&
        cuentaData.planId
    ) {

        await updateDoc(

            doc(
                db,
                "clinics",
                clinicId,
                "pacientes",
                paciente.id,
                "planesTratamiento",
                cuentaData.planId
            ),

            {

                pagado:
                    nuevoPagado,

                saldoPendiente:
                    saldoRestante,

                estadoFinanciero:
                    saldoRestante > 0
                        ? "Pendiente"
                        : "Pagado",
                auditOperationId,

                    ...updateAudit,

            }

        );

    }

    // =====================================
    // ACTUALIZAR CITA
    // SOLAMENTE SI ES UNA CUENTA DE CITA
    // =====================================

    if (
        !esPlanTratamiento &&
        cuentaData.citaId
    ) {

        await updateDoc(

            doc(
                db,
                "clinics",
                clinicId,
                "citas",
                cuentaData.citaId
            ),

            {

                pagado:
                    nuevoPagado,

                saldo:
                    saldoRestante,

                estado:
                    saldoRestante === 0
                        ? "pagada"
                        : "activa",
                auditOperationId,
                    ...updateAudit,

            }

        );

    }

    // REGISTRO ÚNICO DE AUDITORÍA DEL PAGO
    await addDoc(
        collection(
            db,
            "clinics",
            clinicId,
            "logs"
        ),
        {
            action: "PAGO_REGISTRADO",
            module: "Finanzas",
            description:
                `Pago de ${pagoMonto.toLocaleString("es-MX", {
                    style: "currency",
                    currency: "MXN"
                })} registrado para ${
                    `${paciente.nombre || ""} ${paciente.apellidos || ""}`.trim()
                }. Folio: ${folio}. Saldo restante: ${
                    saldoRestante.toLocaleString("es-MX", {
                        style: "currency",
                        currency: "MXN"
                    })
                }. Se actualizó la cuenta por cobrar${
                    cuentaData.citaId
                        ? " y el saldo de la cita"
                        : ""
                }${
                    esPlanTratamiento
                        ? " y el plan de tratamiento"
                        : ""
                }.`,
            userId: usuarioActual.uid,
            userName:
                usuarioActual.displayName ||
                usuarioActual.email ||
                "Usuario sin nombre",
            userEmail: usuarioActual.email || "",
            pacienteId: paciente.id,
            pacienteNombre:
                `${paciente.nombre || ""} ${paciente.apellidos || ""}`.trim(),
            monto: pagoMonto,
            folio,
            cuentaPorCobrarId: cuentaId,
            citaId: cuentaData.citaId || null,
            planId: cuentaData.planId || null,
            auditOperationId,
            createdAt: serverTimestamp()
        }
    );

    // =====================================
    // RESULTADO
    // =====================================

    return {

        folio,

        reciboUrl:
            downloadURL,

        saldoRestante,

        nuevoPagado

    };

}